import { createContext, useContext, useState, useCallback, ReactNode } from 'react'

interface SourceRect {
  top: number
  left: number
  width: number
  height: number
}

interface TransitionState {
  isTransitioning: boolean
  sourceMetric: string | null
  sourceRect: SourceRect | null
}

interface PageTransitionContextValue extends TransitionState {
  startTransition: (metric: string, rect: SourceRect) => void
  resetTransition: () => void
}

const PageTransitionContext = createContext<PageTransitionContextValue | undefined>(undefined)

export const usePageTransition = () => {
  const context = useContext(PageTransitionContext)
  if (!context) {
    throw new Error('usePageTransition must be used within PageTransitionProvider')
  }
  return context
}

interface PageTransitionProviderProps {
  children: ReactNode
}

export const PageTransitionProvider = ({ children }: PageTransitionProviderProps) => {
  const [state, setState] = useState<TransitionState>({
    isTransitioning: false,
    sourceMetric: null,
    sourceRect: null,
  })

  const startTransition = useCallback((metric: string, rect: SourceRect) => {
    setState({
      isTransitioning: true,
      sourceMetric: metric,
      sourceRect: rect,
    })
  }, [])

  const resetTransition = useCallback(() => {
    setState({
      isTransitioning: false,
      sourceMetric: null,
      sourceRect: null,
    })
  }, [])

  return (
    <PageTransitionContext.Provider
      value={{
        ...state,
        startTransition,
        resetTransition,
      }}
    >
      {children}
    </PageTransitionContext.Provider>
  )
}
